import Phaser from 'phaser';
import { COLORS, FONTS, GAME_HEIGHT, GAME_WIDTH, MODAL_BREDDE, fontStr } from '@/config';
import { gameState } from '@/state/store';
import {
  KAMPANJE_KANALER,
  sjangerTreffFor,
  treffEtikett,
  type KampanjeKanal,
  type KampanjeKategori,
} from '@/data/kanaler';
import type { AktivKampanje, Tier } from '@/state/GameState';
import type { ContractedBook } from '@/data/types';
import { KANAL_TIER_KRAV, kanalErLaast } from '@/systems/tier';
import { animateModalGlide } from '@/ui/modalGlide';
import { tooltipManager } from '@/ui/TooltipManager';

const SHEET_WIDTH = MODAL_BREDDE;
const ROW_WIDTH = MODAL_BREDDE - 60;
const ROW_PADDING = 24;
const KORT_GAP = 18;

type Fane = 'kjop' | 'aktive';

const KATEGORI_BORDER: Record<KampanjeKategori, number> = {
  medie: COLORS.wine,
  sosial: 0xd96ba3,
  nisje: COLORS.ochre,
};

const PILLE_FARGE = {
  salg: COLORS.ochre,
  attention: 0x4f8bb8,
  omdomme: COLORS.forest,
  omdommeNeg: 0xa05151,
} as const;

export class MarkedModal {
  private layer?: Phaser.GameObjects.Container;
  private unsubscribe?: () => void;
  private aktivFane: Fane = 'kjop';
  private utvidetKanal: string | null = null;
  private scrollY = 0;
  private scrollMax = 0;

  constructor(private scene: Phaser.Scene) {}

  open(): void {
    if (this.layer) return;
    this.layer = this.scene.add.container(0, 0).setDepth(120);

    const backdrop = this.scene.add
      .rectangle(0, 0, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0.55)
      .setOrigin(0, 0)
      .setInteractive();
    this.layer.add(backdrop);

    this.scene.input.on('wheel', this.onWheel, this);
    this.render();
    animateModalGlide(this.scene, this.layer);
    this.unsubscribe = gameState.subscribe(() => this.render());
  }

  close(): void {
    this.unsubscribe?.();
    this.unsubscribe = undefined;
    this.scene.input.off('wheel', this.onWheel, this);
    this.utvidetKanal = null;
    this.scrollY = 0;
    this.layer?.destroy(true);
    this.layer = undefined;
  }

  get isOpen(): boolean {
    return !!this.layer;
  }

  private render(): void {
    if (!this.layer) return;
    while (this.layer.list.length > 1) {
      const last = this.layer.list[this.layer.list.length - 1];
      this.layer.remove(last, true);
    }

    const snap = gameState.snapshot;
    const sheetX = (GAME_WIDTH - SHEET_WIDTH) / 2;
    const sheetY = 96;
    const sheetH = GAME_HEIGHT - 192;

    const sheet = this.scene.add
      .rectangle(sheetX, sheetY, SHEET_WIDTH, sheetH, COLORS.beige, 0.98)
      .setOrigin(0, 0)
      .setStrokeStyle(3, COLORS.wine, 0.6);
    this.layer.add(sheet);

    const headerY = 132;
    const header = this.scene.add
      .text(
        sheetX + ROW_PADDING,
        headerY,
        `Marked og PR — Aktive kampanjer (${snap.aktiveKampanjer.length})`,
        {
          fontFamily: FONTS.display,
          fontSize: fontStr(38),
          color: '#862736',
          wordWrap: { width: SHEET_WIDTH - ROW_PADDING * 2 - 110 },
        },
      )
      .setOrigin(0, 0);
    this.layer.add(header);

    const subtitle = this.scene.add
      .text(
        sheetX + ROW_PADDING,
        headerY + 116,
        `Dag ${snap.dag} · ${snap.kroner.toLocaleString('nb-NO')} kr`,
        {
          fontFamily: FONTS.body,
          fontSize: fontStr(22),
          color: '#3a2e22',
        },
      )
      .setOrigin(0, 0);
    this.layer.add(subtitle);

    const closeBtn = this.scene.add
      .text(sheetX + SHEET_WIDTH - ROW_PADDING, headerY - 4, '×', {
        fontFamily: FONTS.display,
        fontSize: fontStr(72),
        color: '#1a1a1a',
      })
      .setOrigin(1, 0)
      .setInteractive({ useHandCursor: true });
    closeBtn.on('pointerup', () => this.close());
    this.layer.add(closeBtn);

    const tabY = headerY + 160;
    const tabs = this.byggFaner(sheetX + ROW_PADDING, tabY, snap.aktiveKampanjer.length);
    this.layer.add(tabs);

    const listX = sheetX + (SHEET_WIDTH - ROW_WIDTH) / 2;
    const listTop = tabY + 100;
    const listBunn = sheetY + sheetH - 32;
    const listHoyde = listBunn - listTop;

    const listContainer = this.scene.add.container(0, 0);
    this.layer.add(listContainer);

    const maske = this.scene.make.graphics({ x: 0, y: 0 }, false);
    maske.fillStyle(0xffffff);
    maske.fillRect(listX - 4, listTop, ROW_WIDTH + 8, listHoyde);
    const maskeObj = maske.createGeometryMask();
    listContainer.setMask(maskeObj);

    const tilgjengeligeBoker = this.tilgjengeligeBoker();

    let y = listTop - this.scrollY;
    let bygdHoyde = 0;

    if (this.aktivFane === 'kjop') {
      for (const kanal of KAMPANJE_KANALER) {
        const utvidet = this.utvidetKanal === kanal.id;
        const kort = this.byggKanalKort(
          listX,
          y,
          kanal,
          snap.kroner,
          tilgjengeligeBoker,
          utvidet,
          snap.tier,
        );
        listContainer.add(kort.container);
        y += kort.height + KORT_GAP;
        bygdHoyde += kort.height + KORT_GAP;
      }
    } else {
      if (snap.aktiveKampanjer.length === 0) {
        const tomBoks = this.scene.add
          .rectangle(listX, listTop, ROW_WIDTH, 220, COLORS.white, 0.9)
          .setOrigin(0, 0)
          .setStrokeStyle(2, COLORS.oakDark, 0.4);
        const tomTxt = this.scene.add
          .text(
            listX + ROW_WIDTH / 2,
            listTop + 110,
            'Ingen aktive kampanjer.\nGå til «Kjøp kampanje»-fanen.',
            {
              fontFamily: FONTS.body,
              fontSize: fontStr(24),
              color: '#1a1a1a',
              align: 'center',
            },
          )
          .setOrigin(0.5);
        this.layer.add([tomBoks, tomTxt]);
      } else {
        for (const aktiv of snap.aktiveKampanjer) {
          const rad = this.byggAktivRad(listX, y, aktiv, snap.dag);
          listContainer.add(rad.container);
          y += rad.height + KORT_GAP;
          bygdHoyde += rad.height + KORT_GAP;
        }
      }
    }

    this.scrollMax = Math.max(0, bygdHoyde - listHoyde);
    if (this.scrollY > this.scrollMax) this.scrollY = this.scrollMax;

    this.bindScroll(listX, listTop, listHoyde);
  }

  private bindScroll(listX: number, listTop: number, listHoyde: number): void {
    if (!this.layer) return;
    if (this.scrollMax <= 0) return;
    const sone = this.scene.add
      .rectangle(listX, listTop, ROW_WIDTH, listHoyde, 0x000000, 0)
      .setOrigin(0, 0)
      .setInteractive();
    let dragStart = 0;
    let scrollStart = 0;
    sone.on('pointerdown', (p: Phaser.Input.Pointer) => {
      dragStart = p.y;
      scrollStart = this.scrollY;
    });
    sone.on('pointermove', (p: Phaser.Input.Pointer) => {
      if (!p.isDown) return;
      const delta = dragStart - p.y;
      this.scrollY = Math.max(0, Math.min(this.scrollMax, scrollStart + delta));
      this.render();
    });
    this.layer.add(sone);
  }

  private onWheel = (
    _pointer: Phaser.Input.Pointer,
    _objs: Phaser.GameObjects.GameObject[],
    _dx: number,
    dy: number,
  ): void => {
    if (!this.layer || this.scrollMax <= 0) return;
    this.scrollY = Math.max(0, Math.min(this.scrollMax, this.scrollY + dy));
    this.render();
  };

  private byggFaner(x: number, y: number, antallAktive: number): Phaser.GameObjects.Container {
    const c = this.scene.add.container(x, y);
    const faner: Array<{ id: Fane; label: string }> = [
      { id: 'kjop', label: 'Kjøp kampanje' },
      { id: 'aktive', label: `Aktive (${antallAktive})` },
    ];

    let fx = 0;
    for (const f of faner) {
      const aktiv = f.id === this.aktivFane;
      const tekst = this.scene.add
        .text(0, 0, f.label, {
          fontFamily: FONTS.hud,
          fontSize: fontStr(22),
          color: aktiv ? '#ffffff' : '#3a2e22',
          fontStyle: '700',
        })
        .setOrigin(0, 0);
      const padX = 22;
      const padY = 14;
      const w = tekst.width + padX * 2;
      const h = tekst.height + padY * 2;
      const bg = this.scene.add
        .rectangle(fx, 0, w, h, aktiv ? COLORS.wine : COLORS.white, 1)
        .setOrigin(0, 0)
        .setStrokeStyle(2, COLORS.wine, aktiv ? 0 : 0.6);
      tekst.setPosition(fx + padX, padY);
      c.add([bg, tekst]);
      bg.setInteractive({ useHandCursor: true });
      bg.on('pointerup', () => {
        if (this.aktivFane !== f.id) {
          this.aktivFane = f.id;
          this.scrollY = 0;
          this.render();
        }
      });
      fx += w + 12;
    }
    return c;
  }

  private byggKanalKort(
    x: number,
    y: number,
    kanal: KampanjeKanal,
    kroner: number,
    boker: ContractedBook[],
    utvidet: boolean,
    spillerTier: Tier,
  ): { container: Phaser.GameObjects.Container; height: number } {
    const c = this.scene.add.container(x, y);
    const laast = kanalErLaast(kanal.id, spillerTier);
    const reellUtvidet = laast ? false : utvidet;
    const grunnHoyde = 280;
    const bokSeksjonHoyde = reellUtvidet ? Math.max(120, boker.length * 100 + 30) : 0;
    const hoyde = grunnHoyde + bokSeksjonHoyde;

    const bg = this.scene.add
      .rectangle(0, 0, ROW_WIDTH, hoyde, COLORS.white, 1)
      .setOrigin(0, 0)
      .setStrokeStyle(2, COLORS.oakDark, 0.35);
    if (laast) bg.setAlpha(0.5);
    c.add(bg);

    const venstreBorder = this.scene.add
      .rectangle(0, 0, 4, hoyde, KATEGORI_BORDER[kanal.kategori], 1)
      .setOrigin(0, 0);
    if (laast) venstreBorder.setAlpha(0.5);
    c.add(venstreBorder);

    const navn = this.scene.add
      .text(ROW_PADDING, 18, kanal.navn, {
        fontFamily: FONTS.display,
        fontSize: fontStr(28),
        color: '#1a1a1a',
        wordWrap: { width: ROW_WIDTH - ROW_PADDING * 2 - 240 },
      })
      .setOrigin(0, 0);
    c.add(navn);

    const sesongPris = gameState.sesongKampanjePris(kanal.pris);
    const prisLabel = sesongPris === kanal.pris
      ? `${kanal.pris.toLocaleString('nb-NO')} kr`
      : `${sesongPris.toLocaleString('nb-NO')} kr`;
    const pris = this.scene.add
      .text(ROW_WIDTH - ROW_PADDING, 18, prisLabel, {
        fontFamily: FONTS.display,
        fontSize: fontStr(26),
        color: '#862736',
      })
      .setOrigin(1, 0);
    c.add(pris);

    if (sesongPris !== kanal.pris) {
      const sesongMerke = this.scene.add
        .text(ROW_WIDTH - ROW_PADDING, 64, sesongPris > kanal.pris ? 'sesong +' : 'sesong −', {
          fontFamily: FONTS.hud,
          fontSize: fontStr(14),
          color: sesongPris > kanal.pris ? '#862736' : '#3d5a4d',
          fontStyle: '700',
        })
        .setOrigin(1, 0);
      c.add(sesongMerke);
    }

    const varighet = this.scene.add
      .text(ROW_WIDTH - ROW_PADDING, 90, `${kanal.varighetDager} dager`, {
        fontFamily: FONTS.body,
        fontSize: fontStr(16),
        color: '#3a2e22',
      })
      .setOrigin(1, 0);
    c.add(varighet);

    const beskrivelse = this.scene.add
      .text(ROW_PADDING, 84, kanal.beskrivelse, {
        fontFamily: FONTS.body,
        fontSize: fontStr(18),
        color: '#3a2e22',
        wordWrap: { width: ROW_WIDTH - ROW_PADDING * 2 - 240 },
      })
      .setOrigin(0, 0);
    c.add(beskrivelse);

    const pilleY = 156;
    const piller = this.byggEffektPiller(ROW_PADDING, pilleY, kanal);
    c.add(piller);

    const knappY = grunnHoyde - 80;
    const knappW = ROW_WIDTH - ROW_PADDING * 2;
    if (laast) {
      const krav = KANAL_TIER_KRAV[kanal.id];
      const laasKnapp = this.byggLaastKnapp(ROW_PADDING, knappY, knappW, krav);
      c.add(laasKnapp);
    } else {
      const knapp = this.byggVelgBokKnapp(ROW_PADDING, knappY, knappW, kanal, reellUtvidet);
      c.add(knapp);
    }

    if (reellUtvidet) {
      const seksjonY = grunnHoyde;
      const seksjon = this.byggBokvalgSeksjon(
        ROW_PADDING,
        seksjonY,
        ROW_WIDTH - ROW_PADDING * 2,
        kanal,
        boker,
        kroner,
      );
      c.add(seksjon);
    }

    if (laast) {
      const overlayTekst = this.scene.add
        .text(ROW_WIDTH / 2, grunnHoyde / 2 - 10, `🔒 Krever Tier ${KANAL_TIER_KRAV[kanal.id]}`, {
          fontFamily: FONTS.hud,
          fontSize: fontStr(28),
          color: '#862736',
          backgroundColor: 'rgba(244,234,213,0.95)',
          padding: { left: 18, right: 18, top: 8, bottom: 8 },
          fontStyle: '700',
        })
        .setOrigin(0.5);
      c.add(overlayTekst);
    }

    return { container: c, height: hoyde };
  }

  private byggLaastKnapp(
    x: number,
    y: number,
    w: number,
    krav: Tier,
  ): Phaser.GameObjects.Container {
    const c = this.scene.add.container(x, y);
    const h = 66;
    const bg = this.scene.add.rectangle(0, 0, w, h, COLORS.beigeDark, 1).setOrigin(0, 0);
    bg.setAlpha(0.6);
    const txt = this.scene.add
      .text(w / 2, h / 2, `🔒 Krever Tier ${krav}`, {
        fontFamily: FONTS.hud,
        fontSize: fontStr(22),
        color: '#3a2e22',
        fontStyle: '700',
      })
      .setOrigin(0.5);
    c.add([bg, txt]);
    return c;
  }

  private byggEffektPiller(
    x: number,
    y: number,
    kanal: KampanjeKanal,
  ): Phaser.GameObjects.Container {
    const c = this.scene.add.container(x, y);
    const tekster: Array<{ label: string; farge: number }> = [
      { label: `Salg ×${kanal.effekt.salgsMultiplikator}`, farge: PILLE_FARGE.salg },
      { label: `+${kanal.effekt.attentionBoost} attention`, farge: PILLE_FARGE.attention },
    ];
    const omd = kanal.effekt.omdommeBoost;
    if (omd !== 0) {
      const tegn = omd > 0 ? '+' : '';
      tekster.push({
        label: `${tegn}${omd} omdømme`,
        farge: omd > 0 ? PILLE_FARGE.omdomme : PILLE_FARGE.omdommeNeg,
      });
    }

    let px = 0;
    for (const t of tekster) {
      const padX = 12;
      const padY = 6;
      const tekst = this.scene.add
        .text(0, 0, t.label, {
          fontFamily: FONTS.hud,
          fontSize: fontStr(16),
          color: '#ffffff',
          fontStyle: '700',
        })
        .setOrigin(0, 0);
      const w = tekst.width + padX * 2;
      const h = tekst.height + padY * 2;
      const bg = this.scene.add
        .rectangle(px, 0, w, h, t.farge, 1)
        .setOrigin(0, 0);
      tekst.setPosition(px + padX, padY);
      c.add([bg, tekst]);
      px += w + 8;
    }
    return c;
  }

  private byggVelgBokKnapp(
    x: number,
    y: number,
    w: number,
    kanal: KampanjeKanal,
    utvidet: boolean,
  ): Phaser.GameObjects.Container {
    const c = this.scene.add.container(x, y);
    const h = 66;
    const fill = utvidet ? COLORS.beigeDark : COLORS.wine;
    const farge = utvidet ? '#3a2e22' : '#ffffff';
    const tekst = utvidet ? 'Skjul bøker ↑' : 'Velg bok →';

    const bg = this.scene.add.rectangle(0, 0, w, h, fill, 1).setOrigin(0, 0);
    const txt = this.scene.add
      .text(w / 2, h / 2, tekst, {
        fontFamily: FONTS.hud,
        fontSize: fontStr(22),
        color: farge,
        fontStyle: '700',
      })
      .setOrigin(0.5);
    c.add([bg, txt]);

    bg.setInteractive({ useHandCursor: true });
    bg.on('pointerover', () => bg.setAlpha(0.85));
    bg.on('pointerout', () => bg.setAlpha(1));
    bg.on('pointerup', () => {
      this.utvidetKanal = utvidet ? null : kanal.id;
      this.render();
    });
    return c;
  }

  private byggBokvalgSeksjon(
    x: number,
    y: number,
    w: number,
    kanal: KampanjeKanal,
    boker: ContractedBook[],
    kroner: number,
  ): Phaser.GameObjects.Container {
    const c = this.scene.add.container(x, y);

    if (boker.length === 0) {
      const tom = this.scene.add
        .text(0, 16, 'Ingen utgitte bøker å markedsføre ennå.', {
          fontFamily: FONTS.body,
          fontSize: fontStr(18),
          color: '#6b6b6b',
          fontStyle: 'italic',
        })
        .setOrigin(0, 0);
      c.add(tom);
      return c;
    }

    let by = 12;
    for (const bok of boker) {
      const rad = this.byggBokRad(0, by, w, kanal, bok, kroner);
      c.add(rad.container);
      by += rad.height + 8;
    }
    return c;
  }

  private byggBokRad(
    x: number,
    y: number,
    w: number,
    kanal: KampanjeKanal,
    bok: ContractedBook,
    kroner: number,
  ): { container: Phaser.GameObjects.Container; height: number } {
    const c = this.scene.add.container(x, y);
    const h = 96;

    const bg = this.scene.add
      .rectangle(0, 0, w, h, COLORS.beige, 0.6)
      .setOrigin(0, 0)
      .setStrokeStyle(1, COLORS.oakDark, 0.3);
    c.add(bg);

    const tittel = this.scene.add
      .text(14, 12, bok.title, {
        fontFamily: FONTS.body,
        fontSize: fontStr(18),
        color: '#1a1a1a',
        fontStyle: '700',
        wordWrap: { width: w - 260 },
      })
      .setOrigin(0, 0);
    c.add(tittel);

    const treff = sjangerTreffFor(kanal, bok.genre);
    const etikett = treffEtikett(treff);
    const treffFarge =
      etikett === 'Høyt' ? COLORS.forest : etikett === 'Middels' ? COLORS.ochre : 0xa05151;
    const treffTxt = this.scene.add
      .text(14, 56, `Treff: ${etikett} (×${treff.toFixed(2)})`, {
        fontFamily: FONTS.hud,
        fontSize: fontStr(14),
        color: this.toCss(treffFarge),
        fontStyle: '600',
      })
      .setOrigin(0, 0)
      .setInteractive({ useHandCursor: false });
    c.add(treffTxt);
    const tooltipTxt = treffTooltipTekst(kanal, bok.genre, treff);
    tooltipManager.attach(
      treffTxt,
      () => tooltipTxt,
      () => ({ x: x + 14 + treffTxt.width / 2, y: y + 56 }),
    );

    const sesongPris = gameState.sesongKampanjePris(kanal.pris);
    const harRaad = kroner >= sesongPris;
    const knappW = 220;
    const knappH = 56;
    const knappX = w - knappW - 12;
    const knappY = (h - knappH) / 2;
    const fill = harRaad ? COLORS.wine : 0xcfc6b7;
    const farge = harRaad ? '#ffffff' : '#7a7060';
    const tekst = harRaad
      ? `Kjøp (${sesongPris.toLocaleString('nb-NO')} kr)`
      : 'For lite';

    const kBg = this.scene.add
      .rectangle(knappX, knappY, knappW, knappH, fill, 1)
      .setOrigin(0, 0);
    const kTxt = this.scene.add
      .text(knappX + knappW / 2, knappY + knappH / 2, tekst, {
        fontFamily: FONTS.hud,
        fontSize: fontStr(16),
        color: farge,
        fontStyle: '700',
      })
      .setOrigin(0.5);
    c.add([kBg, kTxt]);

    if (harRaad) {
      kBg.setInteractive({ useHandCursor: true });
      kBg.on('pointerover', () => kBg.setAlpha(0.85));
      kBg.on('pointerout', () => kBg.setAlpha(1));
      kBg.on('pointerup', () => {
        const ok = gameState.kjoepKampanje(kanal.id, bok.isbn);
        if (ok) {
          this.utvidetKanal = null;
          this.aktivFane = 'aktive';
          this.scrollY = 0;
          this.render();
        }
      });
    }

    return { container: c, height: h };
  }

  private byggAktivRad(
    x: number,
    y: number,
    aktiv: AktivKampanje,
    dag: number,
  ): { container: Phaser.GameObjects.Container; height: number } {
    const c = this.scene.add.container(x, y);
    const h = 160;

    const kanal = KAMPANJE_KANALER.find((k) => k.id === aktiv.kanalId);
    const bok = gameState.utgitteBoker.find((b) => b.isbn === aktiv.isbn)
      ?? gameState.iProduksjon.find((b) => b.isbn === aktiv.isbn);

    const bg = this.scene.add
      .rectangle(0, 0, ROW_WIDTH, h, COLORS.white, 1)
      .setOrigin(0, 0)
      .setStrokeStyle(2, COLORS.oakDark, 0.35);
    c.add(bg);

    const venstreBorder = this.scene.add
      .rectangle(0, 0, 4, h, kanal ? KATEGORI_BORDER[kanal.kategori] : COLORS.wine, 1)
      .setOrigin(0, 0);
    c.add(venstreBorder);

    const navn = this.scene.add
      .text(ROW_PADDING, 16, kanal?.navn ?? aktiv.kanalId, {
        fontFamily: FONTS.display,
        fontSize: fontStr(24),
        color: '#1a1a1a',
        wordWrap: { width: ROW_WIDTH - ROW_PADDING * 2 - 220 },
      })
      .setOrigin(0, 0);
    c.add(navn);

    const bokTekst = this.scene.add
      .text(ROW_PADDING, 68, `«${bok?.title ?? aktiv.isbn}»`, {
        fontFamily: FONTS.body,
        fontSize: fontStr(18),
        color: '#3a2e22',
        fontStyle: 'italic',
        wordWrap: { width: ROW_WIDTH - ROW_PADDING * 2 - 220 },
      })
      .setOrigin(0, 0);
    c.add(bokTekst);

    const total = Math.max(1, aktiv.ferdigDag - aktiv.startDag);
    const forløpt = Math.max(0, Math.min(total, dag - aktiv.startDag));
    const igjen = Math.max(0, aktiv.ferdigDag - dag);
    const frac = forløpt / total;

    const meta = this.scene.add
      .text(ROW_WIDTH - ROW_PADDING, 16, `${igjen} dager igjen`, {
        fontFamily: FONTS.hud,
        fontSize: fontStr(16),
        color: '#3a2e22',
        fontStyle: '600',
      })
      .setOrigin(1, 0);
    c.add(meta);

    const salgsTxt = this.scene.add
      .text(ROW_WIDTH - ROW_PADDING, 68, `${aktiv.salgIPerioden.toLocaleString('nb-NO')} solgt`, {
        fontFamily: FONTS.body,
        fontSize: fontStr(16),
        color: '#3a2e22',
      })
      .setOrigin(1, 0);
    c.add(salgsTxt);

    const barY = 124;
    const barX = ROW_PADDING;
    const barW = ROW_WIDTH - ROW_PADDING * 2;
    const barBg = this.scene.add
      .rectangle(barX, barY, barW, 6, COLORS.beigeDark, 1)
      .setOrigin(0, 0);
    const barFyll = this.scene.add
      .rectangle(barX, barY, Math.max(2, Math.round(barW * frac)), 6, COLORS.wine, 1)
      .setOrigin(0, 0);
    c.add([barBg, barFyll]);

    return { container: c, height: h };
  }

  private tilgjengeligeBoker(): ContractedBook[] {
    const snap = gameState.snapshot;
    const samlet: ContractedBook[] = [
      ...snap.iProduksjon.filter((b) => b.status === 'utgitt'),
      ...snap.utgitteBoker,
    ];
    return samlet;
  }

  private toCss(hex: number): string {
    return `#${hex.toString(16).padStart(6, '0')}`;
  }
}

function treffTooltipTekst(
  kanal: KampanjeKanal,
  genre: string,
  treff: number,
): string {
  const treffPct = (treff * 100).toFixed(0);
  const grunnlinje = `Sjanger-treff: ${kanal.navn} × ${genre} = ${treffPct}% av normal salgs-effekt.`;
  if (treff >= 1.3) {
    return `${grunnlinje}\nHøyt treff — kanalen treffer akkurat denne sjangerens lesere.`;
  }
  if (treff >= 0.9) {
    return `${grunnlinje}\nMiddels treff — solid men ikke optimal kombinasjon.`;
  }
  return `${grunnlinje}\nLavt treff — kanalen passer dårlig for denne sjangeren. Velg en mer relevant kanal hvis du kan.`;
}
